/**
 * usePopupCard — singleton state manager for the CML marker popup card.
 *
 * Wave C-α (specs/003-cml-phase2-popup-cards). Implements:
 *   - FR-012   single shared component across both viewers (singleton enforces)
 *   - FR-020   one popup open at a time (singleton design enforces by construction)
 *   - FR-013   marker-tracking positioning (anchorScreenCoords, rAF-driven by T010)
 *   - FR-014   close triggers (Esc / outside / viewport-exit / anchor-hide / toggle)
 *   - FR-025   AbortController-based fetch cancellation (T012 extends)
 *
 * Usage:
 *   import { popupCardState, openPopupCard, closePopupCard, retryPopupCard } from '@/composables/usePopupCard';
 *
 *   // Trigger from a marker click handler:
 *   openPopupCard('cml', cmlId, markerDomElement);
 *
 *   // Bind state in components (e.g., the singleton <CmlPopupCard>):
 *   computed: {
 *     isOpen()    { return popupCardState.isOpen; },
 *     status()    { return popupCardState.status; },
 *     vm()        { return popupCardState.vm; },
 *   }
 *
 * State shape per specs/003-cml-phase2-popup-cards/data-model.md §3.
 *
 * NOTE: This file is created in three milestones during the Foundational phase:
 *   T005 — initial scaffold (this file at creation): state + open/close/retry stubs
 *   T010 — positioning adapter (rAF loop, viewport-exit, anchor-hide detection)
 *   T012 — AbortController fetch cancellation (rapid-clicks race condition)
 */

import Vue from 'vue';
import store from '@/store';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
import httpService from '@/utils/httpService';
import {
  monitoringTabState,
  loadTmlPointsForCml,
} from '@/features/Tags/components/monitoring/useMonitoringTab';

// ─── Reactive singleton state ────────────────────────────────────────────────
// Vue.observable provides Vue-2-compatible reactivity for a plain object.
// All consumers (the singleton <CmlPopupCard> + any marker-click pipelines)
// read/write through this object; Vue's reactivity tracks the access and
// triggers re-renders.
//
// Design choice — Vue.observable vs Vuex (CLAUDE.md Critical Pattern #6):
// The project convention is Vuex with mutations, NOT direct state assignment.
// We intentionally deviate here because popup state is (a) transient (no
// persistence across navigation), (b) single-consumer (one CmlPopupCard reads
// it), (c) feature-local (no cross-feature coordination), and (d) gains no
// value from Vuex DevTools time-travel debugging given its short lifespan.
// Vuex's mutation/action ceremony would be pure overhead here. Vue.observable
// is the same reactive primitive Vuex uses internally — the principle behind
// the project convention (use Vue's reactivity system, not raw object mutation)
// is preserved. If popup state ever grows to need cross-feature wiring or
// persistence, migrate to a Vuex module.
export const popupCardState = Vue.observable({
  isOpen: false,
  entityType: null,                  // 'cml' (Wave C-α); 'tmlpoint' added in follow-up wave
  entityId: null,                    // UUID
  anchorEl: null,                    // HTMLElement the popup tracks
  anchorScreenCoords: null,          // { x: number, y: number } in viewport space, updated by T010 adapter
  status: 'Idle',                    // 'Idle' | 'Loading' | 'Loaded' | 'Error'
  vm: null,                          // CmlPopupViewModel (built by T014 in US1 phase)
  error: null,                       // user-facing error message (set in [Error] state)
  sourceViewer: null,                // '3d' | '360' | 'doc' — which viewer opened this popup (AVA-437)
  preserveSpatialScene: false,       // true only for a popup anchored by the active Spatial scene
});

// ─── Internal helpers ────────────────────────────────────────────────────────
function resetState() {
  popupCardState.isOpen = false;
  popupCardState.entityType = null;
  popupCardState.entityId = null;
  popupCardState.anchorEl = null;
  popupCardState.anchorScreenCoords = null;
  popupCardState.status = 'Idle';
  popupCardState.vm = null;
  popupCardState.error = null;
  popupCardState.sourceViewer = null;
  popupCardState.preserveSpatialScene = false;
}

// T012 — AbortController state for FR-025 (rapid-clicks fetch cancellation).
// `_activeAbort` holds the controller for the current in-flight fetch. The
// fetcher (T013 in US1) reads `getCurrentAbortSignal()` and passes it to
// axios; on every new open() call we abort the prior controller before
// spinning up a new one, so a late-arriving response from a stale request
// never updates the UI.
let _activeAbort = null;

/**
 * Get the AbortSignal that the current pending fetch should attach to. Used
 * by T013's fetcher to thread the signal through to axios:
 *
 *   HttpService.get({ url: ..., signal: getCurrentAbortSignal() })
 *
 * Returns null when no fetch is in-flight (e.g., during retry before T012's
 * open path runs).
 */
export function getCurrentAbortSignal() {
  return _activeAbort ? _activeAbort.signal : null;
}

/**
 * Detect whether an error from an axios call was triggered by AbortController.
 * Axios surfaces aborts as a CanceledError; the fetcher in T013 should call
 * this in its catch block and discard the result if true (do NOT transition
 * the state machine to Error — the popup has already moved on to a new entity).
 */
export function isAbortError(err) {
  if (!err) return false;
  // Native AbortError (some axios versions / fetch fallbacks)
  if (err.name === 'AbortError' || err.name === 'CanceledError') return true;
  // Axios v1 cancel detection
  if (err.code === 'ERR_CANCELED') return true;
  // Defensive: explicit signal aborted check
  if (err.message && /abort|cancel/i.test(err.message)) return true;
  return false;
}

// ─── T013 — CML data fetcher ─────────────────────────────────────────────────
// Threads the AbortSignal from T012 through to axios. If the request is
// aborted (because openPopupCard was called again for a different entity),
// returns null and lets the caller silently discard. Real errors propagate.
async function fetchCml(id) {
  const signal = getCurrentAbortSignal();
  try {
    // X-Facility-Id is required by every /api/v1/ CML endpoint (400 if
    // missing). The header value is the AVA `assetId` (same GUID, four
    // names — see [[project_ava_asset_facility_vocab]]). Matches the
    // facilityHeaders() helper in useMonitoringTab.js — keep them in sync
    // if the convention changes.
    const response = await httpService.get({
      url: `/api/v1/cmls/${id}`,
      signal,
      headers: { 'X-Facility-Id': store.state.assetId },
    });
    return response.data;
  } catch (err) {
    if (isAbortError(err)) {
      debugLog('[usePopupCard] fetchCml aborted (FR-025) — discarding result');
      return null;
    }
    throw err;
  }
}

// ─── T014 — CmlPopupViewModel builder ────────────────────────────────────────
// Maps the raw GET /api/v1/cmls/{id} response into the display-ready shape per
// data-model.md §2.1. Applies FR-018 placeholder substitutions for null /
// missing fields ("—", "Never inspected", "0 TmlPoints"). Sets action-button
// visibility flags from server-supplied permissions.canEdit (FR-019 / Q4).
function buildCmlPopupViewModel(cml) {
  if (!cml) return null;

  const canEdit = !!(cml.permissions && cml.permissions.canEdit);

  return {
    // Identity / routing
    id: cml.id,
    masterTagId: cml.masterTagId,
    facilityId: cml.facilityId,

    // For status-color resolution — popup uses the same object shape the
    // marker pipeline uses (forgeService._getCmlConditionColor reads .status
    // and may also read .conditionRating). Pass through the whole object so
    // the shared color function works without adapter shims.
    raw: cml,

    // Display fields (FR-002, FR-018 placeholders)
    cmlNo: cml.cmlNo || '—',
    cmlType: cml.cmlType || '—',
    worstCrDisplay:
      cml.worstCR_Cached != null ? `${formatDecimal(cml.worstCR_Cached, 3)} mm/yr` : '—',
    minRlDisplay:
      cml.minRL_Cached != null ? `${formatDecimal(cml.minRL_Cached, 1)} yr` : '—',
    currentMinWtDisplay:
      cml.currentMinWT_Cached != null
        ? `${formatDecimal(cml.currentMinWT_Cached, 2)} mm`
        : '—',
    tmlPointsCountDisplay:
      cml.tmlPointsCount != null ? `${cml.tmlPointsCount} TmlPoints` : '—',
    lastInspectionDisplay: cml.lastInspectionDate_Cached
      ? formatDate(cml.lastInspectionDate_Cached)
      : 'Never inspected',

    // Action button visibility (Q4 / FR-019 — hidden, not disabled)
    // View Detail temporarily hidden (2026-05-25): navigation to the master tag
    // Monitoring tab doesn't resolve reliably yet (handoff issue #3 — parent
    // master tag is null in some open flows). Flip back to `true` once fixed.
    showViewDetail: false,
    showAddReading: canEdit,
    showEdit: canEdit,
  };
}

// Small utility helpers — kept local to avoid pulling in a date/decimal lib.
function formatDecimal(value, digits) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toFixed(digits);
}
function formatDate(isoStr) {
  if (!isoStr) return '—';
  try {
    const d = new Date(isoStr);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
  } catch (err) {
    debugWarn('[usePopupCard] formatDate failed for value', isoStr, err);
    return '—';
  }
}

// ─── TmlPoint resolution (TRK-145) ────────────────────────────────────────────
// TmlPoints don't have a dedicated /api/v1/tmlpoints/{id} endpoint — they're
// always fetched as children of a CML. So instead of an HTTP fetch, we resolve
// from the Monitoring tab's existing observable cache (which the tag info
// panel populates as soon as markers render). When the cache is cold we
// trigger `loadTmlPointsForCml(parentCmlId)` and re-resolve once it finishes.
//
// Returning null from this fast-path tells the open() flow to lazy-load and
// retry — same shape as the CML fetcher's abort branch.
function _findTmlPointInCache(tmlPointId) {
  if (!tmlPointId) return null;
  const cache = monitoringTabState.tmlPointsByCml;
  // Cache is keyed by parent CML id; scan all entries (small fan-out — one
  // master tag rarely has more than ~10 CMLs visible).
  // eslint-disable-next-line no-restricted-syntax
  for (const cmlId of Object.keys(cache)) {
    const list = cache[cmlId] || [];
    // eslint-disable-next-line no-restricted-syntax
    for (const tp of list) {
      if (tp && tp.id === tmlPointId) {
        return { tmlPoint: tp, parentCmlId: cmlId };
      }
    }
  }
  return null;
}

// Build the parent-CML mini reference for a TmlPoint popup — used in the
// subtitle line and the "View Parent CML" action.
function _findParentCmlInCache(cmlRecordId) {
  if (!cmlRecordId) return null;
  const cache = monitoringTabState.cmlsByMasterTag;
  // eslint-disable-next-line no-restricted-syntax
  for (const masterTagId of Object.keys(cache)) {
    const list = cache[masterTagId] || [];
    // eslint-disable-next-line no-restricted-syntax
    for (const cml of list) {
      if (cml && cml.id === cmlRecordId) {
        return cml;
      }
    }
  }
  return null;
}

// ─── TmlPopupViewModel builder (TRK-145) ─────────────────────────────────────
// Maps a TmlPoint cache entry into the display-ready shape consumed by the
// CmlPopupCard template's `entityType === 'tmlpoint'` branch. Mirrors the CML
// view-model shape so the popup chrome (head/hero/facts/footer) can re-use
// the same slots without component splitting.
function buildTmlPopupViewModel(tmlPoint, parentCml) {
  if (!tmlPoint) return null;

  const perms = tmlPoint.permissions || {};
  const canEdit = !!perms.canEdit;
  const canAddReading = !!perms.canAddReading;

  // For TmlPoint status color, inherit from the parent CML's conditionRating
  // (TmlPoints don't have their own workflow status — they're measurement
  // POINTS, not monitoring AREAS). This matches the 3D marker color logic in
  // forgeService.js `_getCmlConditionColor` → applied to both CML sphere and
  // child TmlPoint billboard.
  const rawForStatus = parentCml || { conditionRating: null };

  return {
    // Identity / routing
    id: tmlPoint.id,
    cmlRecordId: tmlPoint.cmlRecordId,
    facilityId: tmlPoint.facilityId,

    // For statusColor / statusLabel computation — popup helper reads
    // raw.conditionRating, so we point it at the parent CML's status.
    raw: {
      // Pass the underlying TmlPoint through so the template can reach
      // position / latestReadingValue / latestReadingDate / forgeElementDbId.
      ...tmlPoint,
      // Override status fields with parent CML's so the dot + pill match
      // the parent's color (consistent with the 3D marker).
      conditionRating: rawForStatus.conditionRating,
      parentCml: parentCml || null,
    },

    // Display fields — TmlPoint-specific
    positionLabel: tmlPoint.position || 'No position',
    latestReadingDisplay:
      tmlPoint.latestReadingValue != null
        ? `${formatDecimal(tmlPoint.latestReadingValue, 2)} mm`
        : '—',
    lastReadingDateDisplay: tmlPoint.latestReadingDate
      ? formatDate(tmlPoint.latestReadingDate)
      : 'No readings yet',

    // Action button visibility — FR-019. TmlPoint perms have a slightly
    // different shape than CML (no canViewDetail).
    // View Parent CML temporarily hidden (2026-05-25) — same unreliable master
    // tag / CML navigation as the CML variant. Flip back to `true` once fixed.
    showViewDetail: false,
    showAddReading: canAddReading,
    showEdit: canEdit,
  };
}

// Resolves a TmlPoint from the cache, optionally lazy-loading via the
// parent CML's TmlPoints if the cache is cold. Returns:
//   - { tmlPoint, parentCml } on success
//   - null on abort
// Throws on real errors (caller surfaces in [Error] state).
async function resolveTmlPoint(tmlPointId, parentCmlIdHint) {
  // Fast path: already in cache.
  let hit = _findTmlPointInCache(tmlPointId);
  if (hit) {
    const parentCml = _findParentCmlInCache(hit.parentCmlId);
    return { tmlPoint: hit.tmlPoint, parentCml };
  }

  // Slow path: lazy-load the parent's TmlPoints. Requires the parentCmlId
  // hint from the caller (the marker click handler has it from
  // forgeService._findTmlAtClient's return value).
  if (!parentCmlIdHint) {
    debugWarn('[usePopupCard] TmlPoint not in cache and no parentCmlIdHint provided');
    return { tmlPoint: null, parentCml: null };
  }

  debugLog(`[usePopupCard] TmlPoint ${tmlPointId} not cached — lazy-loading parent ${parentCmlIdHint}`);
  await loadTmlPointsForCml(parentCmlIdHint);

  // Re-resolve after the load completes.
  hit = _findTmlPointInCache(tmlPointId);
  if (!hit) {
    return { tmlPoint: null, parentCml: null };
  }
  const parentCml = _findParentCmlInCache(hit.parentCmlId);
  return { tmlPoint: hit.tmlPoint, parentCml };
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Open the popup anchored to a marker DOM element.
 *
 * T012 extends this to manage AbortController for fetch cancellation. For the
 * T005 scaffold, this just records the requested state and moves to Loading.
 * The CML fetcher (T013) reads (entityType, entityId) and populates vm.
 *
 * @param {'cml'|'tmlpoint'} entityType  CML uses an HTTP fetch; TmlPoint reads
 *   from the Monitoring tab observable cache (lazy-loads via parentCmlId hint
 *   if cold). See TRK-145.
 * @param {string} entityId   UUID of the entity to display.
 * @param {HTMLElement|null} anchorEl  DOM element to anchor the popup to.
 * @param {object} [options]
 * @param {string} [options.parentCmlId]  Required for entityType='tmlpoint'.
 *   The marker click handler already knows this from screen-space hit
 *   detection (`forgeService._findTmlAtClient` returns it); it's a free hint
 *   that lets us skip a `O(allCmls)` scan to find the parent.
 * @param {boolean} [options.preserveSpatialScene=false]  Explicit authority from a Spatial marker
 *   whose existing scene owns the popup anchor.
 */
export function openPopupCard(entityType, entityId, anchorEl, options) {
  debugLog(`[usePopupCard] open(${entityType}, ${entityId})`);

  if (!entityType || !entityId) {
    debugWarn('[usePopupCard] open() called with missing entityType or entityId — ignoring');
    return;
  }

  // FR-014e — double-click toggle close (T026 polish ensures matching
  // entityType+entityId on an already-open popup closes instead of re-fetching).
  // Foundational behavior: if we get a same-entity re-open while currently
  // Loading or Loaded, treat as close — this avoids the AbortController
  // churn the spec's note about FR-014e warns against.
  //
  // EXCEPTION — forceOpen: a row/kebab FOCUS action (useFocusCml) re-selecting the
  // SAME CML must (re)focus it — re-fit + re-highlight — NOT toggle it closed. It sets
  // forceOpen, so we skip the toggle and fall through to the re-open below, which
  // drives a fresh Loading→Loaded cycle that re-fires the cross-viewer sync. A marker
  // click passes no forceOpen, so its click-again-to-dismiss behavior is unchanged.
  if (
    !(options && options.forceOpen) &&
    popupCardState.isOpen &&
    popupCardState.entityType === entityType &&
    popupCardState.entityId === entityId
  ) {
    debugLog('[usePopupCard] same-entity re-open → toggle close');
    closePopupCard();
    return;
  }

  // FR-025 — cancel any in-flight fetch from a prior open() call before
  // initiating the new fetch path. T013's fetcher reads getCurrentAbortSignal()
  // and threads it to axios; aborting here causes the axios promise to reject
  // with a CanceledError, which T013's catch detects via isAbortError() and
  // silently discards.
  if (_activeAbort) {
    debugLog('[usePopupCard] aborting prior in-flight fetch (FR-025)');
    _activeAbort.abort();
    _activeAbort = null;
  }
  _activeAbort = new AbortController();

  popupCardState.entityType = entityType;
  popupCardState.entityId = entityId;
  popupCardState.anchorEl = anchorEl || null;
  popupCardState.vm = null;
  popupCardState.error = null;
  popupCardState.status = 'Loading';
  popupCardState.isOpen = true;
  popupCardState.sourceViewer = (options && options.sourceViewer) || null;
  popupCardState.preserveSpatialScene = !!(options && options.preserveSpatialScene);
  debugLog(`[usePopupCard] open(${entityType}, ${entityId}) sourceViewer=${popupCardState.sourceViewer}`);

  // T013 — fire-and-forget CML fetch. The async work runs in its own scope so
  // openPopupCard stays synchronous and the popup chrome can render
  // immediately (SC-011 <300ms target). On every state update we re-check
  // that we're still on the same entity to defend against the race window
  // between a fetch starting and the user clicking a different marker.
  if (entityType === 'cml') {
    (async () => {
      try {
        const cmlData = await fetchCml(entityId);
        if (cmlData === null) return;                                    // aborted via FR-025
        if (popupCardState.entityId !== entityId) return;                // entity changed during fetch
        popupCardState.vm = buildCmlPopupViewModel(cmlData);
        popupCardState.status = 'Loaded';
        debugLog(`[usePopupCard] fetch complete for cml=${entityId}`);
      } catch (err) {
        if (popupCardState.entityId !== entityId) return;
        debugError('[usePopupCard] fetchCml failed', err);
        popupCardState.error =
          (err && err.response && err.response.data && err.response.data.message) ||
          (err && err.message) ||
          'Failed to load CML data';
        popupCardState.status = 'Error';
      }
    })();
  } else if (entityType === 'tmlpoint') {
    // TRK-145 — TmlPoint popup. No HTTP fetch — TmlPoint data lives in the
    // Monitoring tab cache (the same store the markers were rendered from).
    // If the cache is cold for the parent CML (shouldn't happen if markers
    // are visible, but defensive), `resolveTmlPoint` will lazy-load it.
    const parentCmlIdHint = (options && options.parentCmlId) || null;
    (async () => {
      try {
        const { tmlPoint, parentCml } = await resolveTmlPoint(entityId, parentCmlIdHint);
        if (popupCardState.entityId !== entityId) return;                // user clicked away
        if (!tmlPoint) {
          popupCardState.error = 'TmlPoint not found in cache. Open the master tag panel to load it.';
          popupCardState.status = 'Error';
          return;
        }
        popupCardState.vm = buildTmlPopupViewModel(tmlPoint, parentCml);
        popupCardState.status = 'Loaded';
        debugLog(`[usePopupCard] TmlPoint resolved id=${entityId} parent=${parentCml?.id || 'unknown'}`);
      } catch (err) {
        if (popupCardState.entityId !== entityId) return;
        debugError('[usePopupCard] resolveTmlPoint failed', err);
        popupCardState.error = (err && err.message) || 'Failed to load TmlPoint data';
        popupCardState.status = 'Error';
      }
    })();
  } else {
    debugWarn(`[usePopupCard] entityType=${entityType} is not implemented`);
  }
}

/**
 * Close the popup. Resets all transient state. Idempotent.
 */
export function closePopupCard() {
  if (!popupCardState.isOpen) return;
  debugLog('[usePopupCard] close()');
  // FR-025 — close also aborts the in-flight fetch (the user has signalled
  // they don't want this data anymore). T013's fetcher detects the abort and
  // drops the response without updating UI.
  if (_activeAbort) {
    _activeAbort.abort();
    _activeAbort = null;
  }
  resetState();
}

/**
 * Retry the current fetch (from the [Error] state). T013 will provide the
 * actual fetch logic; for now this just resets status to Loading so the UI
 * can re-render the skeleton and the fetcher can re-invoke.
 */
export function retryPopupCard() {
  if (popupCardState.status !== 'Error') {
    debugWarn('[usePopupCard] retry() called when status is not Error — ignoring');
    return;
  }
  debugLog('[usePopupCard] retry()');
  // Spin a fresh AbortController so T013's fetcher gets a non-aborted signal
  // for the retry attempt.
  if (_activeAbort) {
    _activeAbort.abort();
  }
  _activeAbort = new AbortController();
  popupCardState.error = null;
  popupCardState.status = 'Loading';
}

// ─── T010 — Positioning adapter ──────────────────────────────────────────────
// rAF-driven loop that reads the anchor element's screen-space rect each frame
// and writes the popup's translate coords to a CSS custom property on the
// document root. The popup's outer `.cml-popup-card-host` reads
// `var(--popup-anchor-x)` / `var(--popup-anchor-y)` via transform.
//
// Two auto-close conditions detected here:
//   • FR-014c — anchor leaves the visible viewport
//   • FR-014d — anchor is removed from the DOM (filter / scan-change side effect)
//
// The adapter is started by openPopupCard() and stopped by closePopupCard()
// (we install the hook below in this file rather than back-editing the public
// functions above — the export references remain stable).

let _rafHandle = null;
// AVA-437 #8: last rounded anchor position written. The rAF loop runs every frame,
// but writing style + reassigning anchorScreenCoords every frame causes layout
// thrash (the write dirties layout, forcing the next frame's getBoundingClientRect
// to reflow). Track the last written integer-pixel position and skip the writes
// when the popup hasn't moved.
let _lastAnchorX = null;
let _lastAnchorY = null;

function _tickPositioning() {
  _rafHandle = null;

  if (!popupCardState.isOpen) return;

  const el = popupCardState.anchorEl;

  // FR-014d — anchor was removed from the DOM (filter changed, scan switched,
  // marker un-rendered for any reason). Close.
  if (el && !document.body.contains(el)) {
    debugLog('[usePopupCard] anchor removed from DOM → close');
    closePopupCard();
    return;
  }

  // AVA-437 #8: pause per-frame tracking while the user is actively panning/zooming the
  // 360. The getBoundingClientRect + style writes force layout that thrashes against
  // Pannellum's own per-frame hotspot transforms, making zoom feel sluggish. The popup
  // holds its last position during the gesture and re-tracks (snaps to the anchor) the
  // moment interaction ends — the next non-interacting frame sees changed rounded coords
  // and writes. The cheap DOM-removed close-check above still runs every frame; only the
  // layout-forcing rect read + offscreen check + writes are deferred.
  // AVA-437 #8 (360) + #7a (Forge): pause the layout-forcing rect read while
  // EITHER viewer is actively interacting. 360 = compass pointer/wheel gesture;
  // Forge = camera pan/zoom (Forge dirties layout each frame, so getBoundingClientRect
  // forces a synchronous reflow → the document/3D-viewer lag the user reported).
  const panoInteracting = !!(store.state.panoramaCompassState && store.state.panoramaCompassState.isInteracting);
  const forgeInteracting = !!store.state.forgeViewerInteracting;
  const isInteracting = panoInteracting || forgeInteracting;
  if (el && !isInteracting) {
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;

    // FR-014c — anchor center is outside the visible viewport (with a small
    // margin so the popup doesn't dismiss the moment the marker grazes the
    // edge). Close.
    const margin = 8;
    const offscreen =
      cx < -margin ||
      cy < -margin ||
      cx > window.innerWidth + margin ||
      cy > window.innerHeight + margin;
    if (offscreen) {
      debugLog('[usePopupCard] anchor off-screen → close');
      closePopupCard();
      return;
    }

    // Write the screen coords back to state (consumers can read them) AND
    // mirror them onto the document root so the popup's transform picks them
    // up via CSS variable. Going through document.documentElement avoids
    // per-component refs and lets the singleton popup re-render without
    // needing the ref handle.
    //
    // PERF (AVA-437 #8): only write when the rounded (integer-pixel) position
    // changes. When the popup is stationary the rounded coords are unchanged, so
    // we skip all three writes — keeping the per-frame reads cheap (no layout
    // thrash) and avoiding a wasted reactive-object reassignment. During motion
    // we still write whenever the position crosses a pixel boundary.
    const rx = Math.round(cx);
    const ry = Math.round(cy);
    if (rx !== _lastAnchorX || ry !== _lastAnchorY) {
      _lastAnchorX = rx;
      _lastAnchorY = ry;
      popupCardState.anchorScreenCoords = { x: cx, y: cy };
      document.documentElement.style.setProperty('--popup-anchor-x', `${cx}px`);
      document.documentElement.style.setProperty('--popup-anchor-y', `${cy}px`);
    }
  }

  // Schedule next frame.
  _rafHandle = window.requestAnimationFrame(_tickPositioning);
}

function _startPositioning() {
  if (_rafHandle !== null) return;
  _rafHandle = window.requestAnimationFrame(_tickPositioning);
}

function _stopPositioning() {
  if (_rafHandle !== null) {
    window.cancelAnimationFrame(_rafHandle);
    _rafHandle = null;
  }
  // Reset the CSS variables to off-screen so a stale transform never flashes
  // on the next open.
  document.documentElement.style.setProperty('--popup-anchor-x', '-9999px');
  document.documentElement.style.setProperty('--popup-anchor-y', '-9999px');
  _lastAnchorX = null;
  _lastAnchorY = null;
}

// Wire the lifecycle hooks. We patch openPopupCard / closePopupCard above by
// wrapping them through a side-effect export pattern: assign to the *named*
// exports' module bindings via re-export below. The cleanest way in ES
// modules is to wrap the functions at the bottom — but our T005 scaffold
// already exports the bare functions. Instead, we hook by extending the
// behavior here with a small subscription pattern: every state transition
// that flips isOpen runs through these helpers because Vue.observable's
// reactivity doesn't give us a "watch" hook directly without a Vue instance.
//
// Solution: re-define open/close/retry to call the positioning helpers. Vue.observable
// returns the same object reference so attach a "host" Vue instance once and
// $watch on it. This is the same pattern Vuex 3 uses internally.

const _hostVue = new Vue({
  computed: {
    isOpenMirror() { return popupCardState.isOpen; },
    statusMirror() { return popupCardState.status; },
  },
  watch: {
    isOpenMirror(open) {
      if (open) _startPositioning();
      else _stopPositioning();
    },
    // AVA-437 #7b — the Forge anchor is clamped at click-time, while the card is
    // still the shorter skeleton (Loading). Once the full-height card (with the
    // action bar) renders, re-run the clamp so the now-taller card no longer
    // overflows the viewport bottom. Forge-only: the 360 path uses a different
    // anchor and its own positioning, so leave it untouched. nextTick lets the
    // DOM reflect the full-height card before _updateCmlPopupAnchorPosition
    // measures it via .cml-popup-card-host.offsetHeight.
    statusMirror(s) {
      if (s === 'Loaded' && popupCardState.sourceViewer === '3d') {
        debugLog('[usePopupCard] Loaded (3d) → re-clamp Forge popup anchor at full height');
        Vue.nextTick(() => store.state.forgeService?._updateCmlPopupAnchorPosition?.());
      }
    },
  },
});

// Keep a hard reference to the instance so it doesn't get GC'd. The instance
// has no template — it exists purely for its reactive watcher.
export const _internalHost = _hostVue;

// ─── Dev-only console testing fixture ────────────────────────────────────────
// Vue CLI bundles modules with webpack; they are not directly importable via
// dynamic `import('/src/...')` from the browser console (unlike Vite). For
// smoke-testing the popup chrome before US1 wires real marker-click handlers,
// expose the public API on window in non-production builds. Webpack's
// DefinePlugin replaces `process.env.NODE_ENV` at build time, so this block
// becomes dead code in production builds and is eliminated by minification.
//
// Console usage (dev only):
//   window.__cmlPopupCard.openPopupCard('cml', 'test-id', document.querySelector('button'));
//   window.__cmlPopupCard.closePopupCard();
//   window.__cmlPopupCard.popupCardState;   // inspect reactive state
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  window.__cmlPopupCard = {
    openPopupCard,
    closePopupCard,
    retryPopupCard,
    popupCardState,
    getCurrentAbortSignal,
    isAbortError,
  };
}
